import { computeMarginPrice } from "../helpers";
import { computeChildrenQuoteRequestLineCosts } from "./computeChildrenQuoteRequestLineCosts";
import { ComputationOptions } from "./computeMaterialPrice";
import { OptimizedMachineTimes } from "./computeOptimizedMachineTimes";
import { OptimizedMaterialCosts } from "./computeOptimizedMaterialCosts";
import {
  computeQuoteRequestLinePrice,
  QuoteRequestLineForComputing,
} from "./computeQuoteRequestLinePrice";
import { AssemblyWithChildren } from "./groupQuoteRequestLinesByBatch";

export const computeAssemblyPrice = (
  {
    children,
    quoteRequestLine,
    salesMarginRatio,
    isQuotePrice = false,
    optimizedMachineTimes,
    optimizedMaterialCosts,
  }: {
    children: AssemblyWithChildren<QuoteRequestLineForComputing>[];
    quoteRequestLine: QuoteRequestLineForComputing;
    salesMarginRatio: number;
    isQuotePrice?: boolean;
    optimizedMachineTimes: OptimizedMachineTimes;
    optimizedMaterialCosts: OptimizedMaterialCosts;
  },
  options: ComputationOptions,
): {
  assembly: {
    cost: number;
    margin: number;
    price: number;
    costWithSalesMargin: number;
    marginWithSalesMargin: number;
    priceWithSalesMargin: number;
    seriesCost: number;
    seriesMargin: number;
    seriesPrice: number;
    seriesCostWithSalesMargin: number;
    seriesMarginWithSalesMargin: number;
    seriesPriceWithSalesMargin: number;
  };
  parentQuoteRequestLine: {
    unitCost: number;
    unitMarginPrice: number;
    unitPrice: number;
    cost: number;
    margin: number;
    price: number;
    seriesCost: number;
    seriesMargin: number;
    seriesPrice: number;
  };
} => {
  const {
    partCost: parentAssemblyPartCost,
    partMarginPrice: parentAssemblyPartMarginPrice,
    partPrice: parentAssemblyPartPrice,
    assembly: {
      totalCost: parentAssemblyCost,
      totalMargin: parentAssemblyMargin,
      totalPrice: parentAssemblyPrice,
    },
  } = computeQuoteRequestLinePrice(
    {
      quoteRequestLine,
      isQuotePrice,
      optimizedMachineTimes,
      optimizedMaterialCosts,
    },
    options,
  );

  const { childrenTotalCost, childrenTotalMargin, childrenTotalPrice } =
    computeChildrenQuoteRequestLineCosts(
      {
        children,
        isQuotePrice,
        optimizedMachineTimes,
        optimizedMaterialCosts,
      },
      options,
    );

  const assemblyCost = parentAssemblyCost + childrenTotalCost;
  const assemblyMargin = parentAssemblyMargin + childrenTotalMargin;
  const assemblyPrice = parentAssemblyPrice + childrenTotalPrice;

  const assemblyCostWithSalesMargin =
    assemblyCost +
    computeMarginPrice({
      cost: assemblyCost,
      marginRatio: salesMarginRatio,
    });
  const assemblyMarginWithSalesMargin = assemblyCostWithSalesMargin - assemblyCost;
  const assemblyPriceWithSalesMargin =
    assemblyPrice +
    computeMarginPrice({
      cost: assemblyPrice,
      marginRatio: salesMarginRatio,
    });

  const assemblySeriesCost = assemblyCost * quoteRequestLine.quantity;
  const assemblySeriesMargin = assemblyMargin * quoteRequestLine.quantity;
  const assemblySeriesPrice = assemblyPrice * quoteRequestLine.quantity;

  const assemblySeriesCostWithSalesMargin =
    assemblySeriesCost +
    computeMarginPrice({
      cost: assemblySeriesCost,
      marginRatio: salesMarginRatio,
    });
  const assemblySeriesMarginWithSalesMargin =
    assemblySeriesCostWithSalesMargin - assemblySeriesCost;
  const assemblySeriesPriceWithSalesMargin =
    assemblySeriesPrice +
    computeMarginPrice({
      cost: assemblySeriesPrice,
      marginRatio: salesMarginRatio,
    });

  return {
    assembly: {
      cost: assemblyCost,
      margin: assemblyMargin,
      price: assemblyPrice,
      costWithSalesMargin: assemblyCostWithSalesMargin,
      marginWithSalesMargin: assemblyMarginWithSalesMargin,
      priceWithSalesMargin: assemblyPriceWithSalesMargin,
      seriesCost: assemblySeriesCost,
      seriesMargin: assemblySeriesMargin,
      seriesPrice: assemblySeriesPrice,
      seriesCostWithSalesMargin: assemblySeriesCostWithSalesMargin,
      seriesMarginWithSalesMargin: assemblySeriesMarginWithSalesMargin,
      seriesPriceWithSalesMargin: assemblySeriesPriceWithSalesMargin,
    },
    parentQuoteRequestLine: {
      unitCost: parentAssemblyPartCost,
      unitMarginPrice: parentAssemblyPartMarginPrice,
      unitPrice: parentAssemblyPartPrice,
      cost: parentAssemblyCost,
      margin: parentAssemblyMargin,
      price: parentAssemblyPrice,
      seriesCost: parentAssemblyCost * quoteRequestLine.quantity,
      seriesMargin: parentAssemblyMargin * quoteRequestLine.quantity,
      seriesPrice: parentAssemblyPrice * quoteRequestLine.quantity,
    },
  };
};
